// ==================== XSS PROTECTION ====================

export function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text || ''
  return div.innerHTML
}

// ==================== DATE FORMATTING ====================

export function formatDate(dateString) {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    const d = String(date.getDate()).padStart(2, '0')
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const y = date.getFullYear()
    return `${d}.${m}.${y}`
  } catch {
    return dateString
  }
}

// ==================== MAINTENANCE STATUS ====================

/**
 * Classify a piece of equipment against its next maintenance date.
 * Used by the admin equipment list and by the mechanic's service card.
 *
 * @param {string|Date|null} nextMaintenanceDate
 * @param {number} soonThresholdDays  days ahead that still count as "due soon"
 * @returns {{status: 'none'|'ok'|'soon'|'overdue', days: number|null}}
 *          `days` is days remaining (negative when overdue), null when unset.
 */
export function maintenanceStatus(nextMaintenanceDate, soonThresholdDays = 7) {
  if (!nextMaintenanceDate) return { status: 'none', days: null }

  const next = new Date(nextMaintenanceDate)
  if (isNaN(next.getTime())) return { status: 'none', days: null }

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  const days = Math.round((startOfDay(next) - startOfDay(new Date())) / MS_PER_DAY)

  if (days < 0) return { status: 'overdue', days }
  if (days <= soonThresholdDays) return { status: 'soon', days }
  return { status: 'ok', days }
}

/**
 * Add `intervalDays` to `fromDate` and return an ISO date string (YYYY-MM-DD),
 * or null when there is no interval configured.
 */
export function addDays(fromDate, intervalDays) {
  if (!fromDate || !intervalDays || intervalDays <= 0) return null

  const base = new Date(fromDate)
  if (isNaN(base.getTime())) return null

  const result = new Date(base.getFullYear(), base.getMonth(), base.getDate() + Number(intervalDays))
  const m = String(result.getMonth() + 1).padStart(2, '0')
  const d = String(result.getDate()).padStart(2, '0')
  return `${result.getFullYear()}-${m}-${d}`
}

// ==================== PASSWORD GENERATION ====================

// Strong password with uppercase, lowercase, numbers and symbols (for mechanic credentials)
export function generateStrongPassword(length = 12) {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghjkmnpqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*'
  const all = uppercase + lowercase + numbers + symbols

  let password = ''
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]

  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)]
  }

  return password.split('').sort(() => Math.random() - 0.5).join('')
}

// Simple alphanumeric password (for tenant admin accounts)
export function generatePassword(length = 12) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let password = ''
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return password
}

// ==================== NFC TAG GENERATION ====================

export function generateNFCId(tenantId, index) {
  const shortId = tenantId.substring(0, 8)
  const timestamp = Date.now().toString(36)
  const indexStr = index.toString().padStart(3, '0')
  return `NFC-${shortId}-${timestamp}-${indexStr}`
}

// Tags generated into the unassigned warehouse pool have no tenant yet,
// so the id can't embed a tenant prefix like generateNFCId() does.
export function generateWarehouseNFCId(index) {
  const timestamp = Date.now().toString(36)
  const indexStr = index.toString().padStart(4, '0')
  return `NFC-WH-${timestamp}-${indexStr}`
}

export function generateUrlCode() {
  return Math.random().toString(36).substring(2, 15) +
         Math.random().toString(36).substring(2, 15)
}

// ==================== CLIPBOARD ====================

export function copyToClipboard(elementId, btnElement) {
  const element = document.getElementById(elementId)
  const text = element.textContent

  navigator.clipboard.writeText(text).then(() => {
    const originalText = btnElement.textContent
    btnElement.textContent = '✓ Copied!'
    setTimeout(() => {
      btnElement.textContent = originalText
    }, 2000)
  })
}

// ==================== IMAGE COMPRESSION ====================

export async function compressImage(file, maxWidth = 800, quality = 0.7) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width)
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/jpeg', quality)
      }
    }
  })
}
